import * as React from "react"
import { ChevronDownIcon } from "lucide-react"
import { Accordion as AccordionPrimitive } from "radix-ui"

import { cn } from "@/lib/utils"

function Accordion({
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Root>) {
  return <AccordionPrimitive.Root data-slot="accordion" {...props} />
}

function AccordionItem({
  className,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      // `border-border`, not a bare `border-b`: this is Tailwind v4, where the default border colour is
      // `currentColor` rather than v3's gray-200 — so the unqualified utility painted the divider in the
      // foreground text colour (#030712), full-width, 110px from a `Separator` drawing the same rule in
      // the shared `--border` hairline. Guard: `accordion.spec.tsx`. (That hairline used to be quoted
      // here as a hex; it moved on 2026-08-17 when `--border` was raised to clear WCAG 1.4.11 — see
      // `00-variables.css`. Naming the token instead is what keeps this comment true through a retune.)
      className={cn("border-b border-border last:border-b-0", className)}
      {...props}
    />
  )
}

function AccordionTrigger({
  className,
  children,
  headingLevel,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger> & {
  /**
   * `SETTINGS-101`. The heading level Radix wraps the trigger in. Radix's `Header` is an `<h3>`, which
   * is right for an accordion nested under a section `<h2>` and wrong for one that sits directly under
   * the page `<h1>` — the settings shell's accordion branch, i.e. the layout every phone gets, whose
   * outline therefore ran h1 → h3 while the rail branch of the same page emitted a real `<h2>`.
   *
   * A LEVEL change on the element that already exists, never new markup: the section name may be
   * emitted by exactly one branch (`SETTINGS-51`), so nothing may be added beside the header.
   *
   * Left undefined everywhere else on purpose. The default is what almost every accordion in the
   * product wants, and a call site that changes it is asserting something about the outline ABOVE it
   * that only that call site can know.
   */
  headingLevel?: 2
}) {
  const trigger = (
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          "flex flex-1 items-start justify-between gap-4 rounded-md py-4 text-left text-sm font-medium transition-all outline-none hover:underline focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]>svg]:rotate-180",
          className
        )}
        {...props}
      >
        {children}
        <ChevronDownIcon className="pointer-events-none size-4 shrink-0 translate-y-0.5 text-muted-foreground transition-transform duration-200" />
      </AccordionPrimitive.Trigger>
  )

  // `asChild` rather than a second wrapper: the heading must BE the header Radix manages, or the
  // trigger stops being the heading's content and the level says nothing about it.
  if (headingLevel === 2) {
    return (
      <AccordionPrimitive.Header asChild>
        <h2 className="flex">{trigger}</h2>
      </AccordionPrimitive.Header>
    )
  }

  return <AccordionPrimitive.Header className="flex">{trigger}</AccordionPrimitive.Header>
}

function AccordionContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className="overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
      {...props}
    >
      <div className={cn("pt-0 pb-4", className)}>{children}</div>
    </AccordionPrimitive.Content>
  )
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
